import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { sanitizeRedirect } from "../../lib/redirect";
import AuthShell from "./AuthShell";

function formatError(detail) {
  if (!detail) return "Não foi possível entrar. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(" ");
  return detail.msg || String(detail);
}

export default function LoginPage() {
  const { login, loginEmail } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = sanitizeRedirect(params.get("redirect"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await loginEmail(email.trim(), password);
      nav(redirect, { replace: true });
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Bem-vinda de volta"
      subtitle="Acesse sua conta para experimentar joias e curar sua wishlist."
      footer={
        <span>
          Ainda não tem conta?{" "}
          <Link to={`/cadastro${redirect !== "/" ? `?redirect=${encodeURIComponent(redirect)}` : ""}`} className="auth-link" data-testid="link-to-register">Cadastre-se</Link>
        </span>
      }
    >
      {/* Google quick-login */}
      <button
        type="button"
        onClick={() => login(redirect)}
        className="auth-cta auth-cta-secondary mb-2"
        data-testid="login-google-btn"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M22.5 12.27c0-.78-.07-1.53-.2-2.27H12v4.51h5.92c-.25 1.37-1 2.53-2.13 3.31v2.75h3.44c2.02-1.86 3.18-4.6 3.18-7.86z" fill="#4285F4"/>
          <path d="M12 23c2.88 0 5.3-.95 7.07-2.58l-3.44-2.67c-.95.64-2.17 1.02-3.63 1.02-2.79 0-5.16-1.88-6-4.42H2.43v2.78A10.97 10.97 0 0 0 12 23z" fill="#34A853"/>
          <path d="M6 14.36a6.6 6.6 0 0 1 0-4.21V7.36H2.43A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.95L6 14.36z" fill="#FBBC05"/>
          <path d="M12 5.4c1.57 0 2.98.54 4.09 1.6l3.06-3.06C17.3 2.18 14.88 1.2 12 1.2A10.97 10.97 0 0 0 2.43 7.36L6 10.15c.84-2.54 3.21-4.75 6-4.75z" fill="#EA4335"/>
        </svg>
        Entrar com Google
      </button>

      <div className="auth-divider">ou</div>

      <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
        <div>
          <label className="auth-label" htmlFor="email">E-mail</label>
          <input
            id="email" type="email" required autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className={`auth-input ${error ? "error" : ""}`}
            data-testid="login-email-input"
          />
        </div>
        <div>
          <label className="auth-label" htmlFor="password">Senha</label>
          <input
            id="password" type="password" required autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className={`auth-input ${error ? "error" : ""}`}
            data-testid="login-password-input"
          />
        </div>

        {error && (
          <p className="text-sm text-[#B85050]" data-testid="login-error">{error}</p>
        )}

        <button type="submit" disabled={loading} className="auth-cta" data-testid="login-submit-btn">
          {loading ? "Entrando…" : "Entrar"}
        </button>

        <p className="text-center pt-2">
          <Link to="/esqueci-senha" className="auth-link text-sm" data-testid="link-to-forgot">Esqueci minha senha</Link>
        </p>
      </form>
    </AuthShell>
  );
}
